import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle, ArrowRight, Phone, Clock } from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// UTM storage key
const UTM_STORAGE_KEY = 'sylva_utm_params';

// Capture and persist UTM params on first arrival
const captureUtmParams = () => {
  if (typeof window === 'undefined') return;
  
  const params = new URLSearchParams(window.location.search);
  const newUtm = {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
    gclid: params.get('gclid')
  };
  
  // Only store if we have at least one UTM param
  const hasUtm = Object.values(newUtm).some(v => v !== null);
  
  if (hasUtm) {
    // Store in sessionStorage (persists across page navigation, cleared on tab close)
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(newUtm));
  }
};

// Get stored UTM params
const getStoredUtmParams = () => {
  if (typeof window === 'undefined') return {};
  
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Return only non-null values
      return Object.fromEntries(
        Object.entries(parsed).filter(([_, v]) => v !== null)
      );
    }
  } catch (e) {
    console.error('Error reading UTM params:', e);
  }
  
  return {};
};

// Initialize UTM capture on module load
if (typeof window !== 'undefined') {
  captureUtmParams();
}

const LeadForm = ({ 
  formType = "estimation",
  title = "Demander une analyse",
  submitButtonText = "Envoyer ma demande",
  showSurface = true,
  showCadastre = true,
  showEssences = true,
  showAccessibility = true,
  showObjective = true,
  showTiming = true,
  showOwnership = false,
  showPrice = false,
  showIndivisaires = false,
  showSuccessionStatus = false,
  showForestType = false,
  showRecentCut = false,
  showSellProperty = false,
  objectiveOptions = [],
  customFields = null
}) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    commune: "",
    department: "",
    surface: "",
    cadastre: "",
    essences: "",
    accessibility: "",
    objective: "",
    timing: "",
    message: "",
    consent: false,
    ownership: "",
    desiredPrice: "",
    indivisaires: "",
    successionStatus: "",
    forestType: "",
    recentCut: "",
    sellProperty: "",
    parcelsCount: "",
    lastCutPeriod: "",
    lastCutYear: "",
    standAge: "",
    accessType: "",
    loggingTruckAccess: "",
    website: "" // Honeypot
  });
  
  const [utmParams, setUtmParams] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [leadResponse, setLeadResponse] = useState(null);

  useEffect(() => {
    // Capture UTM on component mount (in case page was navigated to)
    captureUtmParams();
    // Get stored UTM params
    setUtmParams(getStoredUtmParams());
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check
    if (formData.website) {
      setIsSuccess(true);
      return;
    }
    
    if (!formData.consent) {
      toast.error("Veuillez accepter les conditions pour envoyer votre demande.");
      return;
    }

    setIsSubmitting(true);

    const intentMap = {
      "valeur": "valeur",
      "vente_foncier": "vente_foncier",
      "vente_bois": "vente_bois",
      "succession": "succession",
      "regroupement": "regroupement",
      "autre": "autre"
    };

    const timingMap = {
      "urgent": "urgent",
      "3_mois": "3_mois",
      "12_mois": "12_mois",
      "futur": "futur",
      "information": "information"
    };

    const leadData = {
      lead_type: formType,
      lead_intent: intentMap[formData.objective] || formData.objective || "non_specifie",
      lead_timing: timingMap[formData.timing] || formData.timing || "non_specifie",
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      email: formData.email,
      commune: formData.commune,
      department: formData.department,
      surface: formData.surface || null,
      cadastre: formData.cadastre || null,
      essences: formData.essences || null,
      accessibility: formData.accessibility || null,
      objective: formData.objective || null,
      timing: formData.timing || null,
      message: formData.message || null,
      ownership: formData.ownership || null,
      desiredPrice: formData.desiredPrice || null,
      indivisaires: formData.indivisaires || null,
      successionStatus: formData.successionStatus || null,
      forestType: formData.forestType || null,
      recentCut: formData.recentCut || null,
      sellProperty: formData.sellProperty || null,
      parcelsCount: formData.parcelsCount || null,
      lastCutPeriod: formData.lastCutPeriod || null,
      lastCutYear: formData.lastCutYear || null,
      standAge: formData.standAge || null,
      accessType: formData.accessType || null,
      loggingTruckAccess: formData.loggingTruckAccess || null,
      ...utmParams,
      website: formData.website,
      consent: formData.consent
    };

    try {
      const response = await axios.post(`${API}/lead`, leadData);

      if (response.data.status === "success") {
        // CRITICAL: Only trigger conversion if ALL conditions are met:
        // 1. status === "success"
        // 2. accepted === true (not honeypot)
        // 3. lead_id exists (real lead was created)
        const isRealLead = response.data.accepted === true && response.data.lead_id;
        
        if (isRealLead && typeof window.gtag === 'function') {
          // Google Ads conversion - ONLY for real accepted leads
          window.gtag('event', 'conversion', {
            'send_to': 'AW-18451239215/lqH8CLm22vccEK-int5E',
            'value': 1.0,
            'currency': 'EUR'
          });
          
          // GA4/Analytics event (NOT a Google Ads conversion)
          // Only non-PII data: lead_type, lead_intent, lead_timing, lead_score
          // NO: name, email, phone, cadastre, address
          window.gtag('event', 'lead_submitted', {
            'event_category': 'lead_generation',
            'lead_type': response.data.lead_type,
            'lead_intent': response.data.lead_intent,
            'lead_timing': response.data.lead_timing,
            'lead_score': response.data.lead_score
          });
        }

        setLeadResponse(response.data);
        setIsSuccess(true);
      }
    } catch (error) {
      // NO conversion triggered on error
      console.error("Error sending lead form:", error);
      if (error.response?.status === 429) {
        toast.error("Trop de requêtes. Veuillez réessayer dans quelques minutes.");
      } else {
        toast.error("Une erreur est survenue. Veuillez réessayer ou nous contacter directement.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const inputClass = "w-full px-4 py-3 bg-white border border-gray-200 rounded-none font-sans text-forest placeholder:text-gray-400 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors";
  const selectClass = "w-full px-4 py-3 bg-white border border-gray-200 rounded-none font-sans text-forest cursor-pointer focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors";

  const getSuccessCTA = () => {
    switch (formType) {
      case "estimation":
        return { text: "En savoir plus sur nos services", link: "/#services" };
      case "vente_foncier":
        return { text: "Découvrir le remembrement forestier", link: "/remembrement-forestier" };
      case "vente_bois":
        return { text: "Vendre également la propriété ?", link: "/vendre-parcelle-bois" };
      case "succession":
        return { text: "Comprendre les successions forestières", link: "/successions-forestieres" };
      default:
        return { text: "Retour à l'accueil", link: "/" };
    }
  };

  if (isSuccess) {
    const cta = getSuccessCTA();
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-bone p-8 md:p-12 text-center"
        data-testid="success-screen"
      >
        <div className="w-16 h-16 bg-forest rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-gold" />
        </div>
        
        <h3 className="font-serif text-2xl md:text-3xl text-forest mb-4">
          Merci. Votre demande a bien été transmise à Sylva Invest.
        </h3>
        
        <p className="font-sans text-gray-600 mb-8 max-w-md mx-auto">
          Les dossiers nécessitant une action rapide sont examinés en priorité.
        </p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          <div className="flex items-center text-sm text-gray-500">
            <Clock className="w-4 h-4 mr-2" />
            <span>Réponse sous 48h</span>
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <Phone className="w-4 h-4 mr-2" />
            <span>+33 6 81 48 12 37</span>
          </div>
        </div>
        
        <Link
          to={cta.link}
          className="inline-flex items-center font-sans text-sm font-medium text-forest hover:text-gold transition-colors"
        >
          {cta.text}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="bg-bone p-8 md:p-10"
    >
      {title && (
        <h3 className="font-serif text-2xl text-forest mb-6">{title}</h3>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-5" data-testid={`lead-form-${formType}`}>
        {/* Honeypot */}
        <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
          <label htmlFor={`website-${formType}`}>Ne pas remplir</label>
          <input type="text" name="website" id={`website-${formType}`} value={formData.website} onChange={handleChange} tabIndex={-1} autoComplete="off" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input type="text" name="firstName" placeholder="Prénom *" value={formData.firstName} onChange={handleChange} required maxLength={100} className={inputClass} data-testid="input-firstName" />
          <input type="text" name="lastName" placeholder="Nom *" value={formData.lastName} onChange={handleChange} required maxLength={100} className={inputClass} data-testid="input-lastName" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input type="tel" name="phone" placeholder="Téléphone *" value={formData.phone} onChange={handleChange} required maxLength={20} className={inputClass} data-testid="input-phone" />
          <input type="email" name="email" placeholder="Email *" value={formData.email} onChange={handleChange} required maxLength={254} className={inputClass} data-testid="input-email" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input type="text" name="commune" placeholder="Commune de la forêt *" value={formData.commune} onChange={handleChange} required maxLength={200} className={inputClass} data-testid="input-commune" />
          <input type="text" name="department" placeholder="Département *" value={formData.department} onChange={handleChange} required maxLength={100} className={inputClass} data-testid="input-department" />
        </div>

        {showSurface && (
          <input type="text" name="surface" placeholder="Surface approximative (hectares)" value={formData.surface} onChange={handleChange} maxLength={50} className={inputClass} data-testid="input-surface" />
        )}

        {showIndivisaires && (
          <input type="text" name="parcelsCount" placeholder="Nombre approximatif de parcelles" value={formData.parcelsCount} onChange={handleChange} maxLength={50} className={inputClass} data-testid="input-parcelsCount" />
        )}

        {showCadastre && (
          <input type="text" name="cadastre" placeholder="Références cadastrales (facultatif)" value={formData.cadastre} onChange={handleChange} maxLength={500} className={inputClass} data-testid="input-cadastre" />
        )}

        {showEssences && (
          <input type="text" name="essences" placeholder="Essences principales si connues (facultatif)" value={formData.essences} onChange={handleChange} maxLength={500} className={inputClass} data-testid="input-essences" />
        )}

        {showForestType && (
          <select name="forestType" value={formData.forestType} onChange={handleChange} className={selectClass} data-testid="select-forestType">
            <option value="">Type de peuplement...</option>
            <option value="plantation">Plantation</option>
            <option value="taillis">Taillis</option>
            <option value="futaie">Futaie</option>
            <option value="mixte">Mixte</option>
            <option value="inconnu">Je ne sais pas</option>
          </select>
        )}

        {showRecentCut && formType !== "vente_bois" && (
          <select name="recentCut" value={formData.recentCut} onChange={handleChange} className={selectClass} data-testid="select-recentCut">
            <option value="">Une coupe a-t-elle été réalisée récemment ?</option>
            <option value="oui">Oui</option>
            <option value="non">Non</option>
            <option value="inconnu">Je ne sais pas</option>
          </select>
        )}

        {showRecentCut && formType === "vente_bois" && (
          <>
            <select name="lastCutPeriod" value={formData.lastCutPeriod} onChange={handleChange} className={selectClass} data-testid="select-lastCutPeriod">
              <option value="">Une coupe a-t-elle été réalisée récemment ?</option>
              <option value="less_than_10_years">Oui, il y a moins de 10 ans</option>
              <option value="more_than_10_years">Non, il y a plus de 10 ans</option>
              <option value="never_known">Jamais à ma connaissance</option>
              <option value="unknown">Je ne sais pas</option>
            </select>

            {(formData.lastCutPeriod === "less_than_10_years" || formData.lastCutPeriod === "more_than_10_years") && (
              <input
                type="text"
                name="lastCutYear"
                value={formData.lastCutYear}
                onChange={handleChange}
                placeholder="En quelle année environ la dernière coupe a-t-elle été réalisée ? (ex: 2018)"
                className={inputClass}
                maxLength={4}
                pattern="[0-9]{4}"
                data-testid="input-lastCutYear"
              />
            )}

            <select name="standAge" value={formData.standAge} onChange={handleChange} className={selectClass} data-testid="select-standAge">
              <option value="">Quel est l'âge approximatif du peuplement ?</option>
              <option value="under_10">Moins de 10 ans</option>
              <option value="10_20">10 à 20 ans</option>
              <option value="20_40">20 à 40 ans</option>
              <option value="40_60">40 à 60 ans</option>
              <option value="over_60">Plus de 60 ans</option>
              <option value="mixed">Plusieurs âges différents</option>
              <option value="unknown">Je ne sais pas</option>
            </select>
          </>
        )}

        {showAccessibility && formType !== "vente_bois" && (
          <select name="accessibility" value={formData.accessibility} onChange={handleChange} className={selectClass} data-testid="select-accessibility">
            <option value="">La parcelle est-elle facilement accessible ?</option>
            <option value="oui">Oui</option>
            <option value="non">Non</option>
            <option value="inconnu">Je ne sais pas</option>
          </select>
        )}

        {showAccessibility && formType === "vente_bois" && (
          <>
            <select name="accessType" value={formData.accessType} onChange={handleChange} className={selectClass} data-testid="select-accessType">
              <option value="">La parcelle est-elle directement accessible depuis une voie ou un chemin carrossable ?</option>
              <option value="paved_or_drivable">Oui, par un chemin empierré ou une voie carrossable</option>
              <option value="unpaved_path">Oui, mais uniquement par un chemin non empierré</option>
              <option value="difficult_or_indirect">Non, l'accès est difficile ou indirect</option>
              <option value="unknown">Je ne sais pas</option>
            </select>

            <select name="loggingTruckAccess" value={formData.loggingTruckAccess} onChange={handleChange} className={selectClass} data-testid="select-loggingTruckAccess">
              <option value="">Un camion forestier peut-il accéder à proximité immédiate de la parcelle ?</option>
              <option value="yes">Oui</option>
              <option value="no">Non</option>
              <option value="unknown">Je ne sais pas</option>
            </select>
          </>
        )}

        {showOwnership && (
          <select name="ownership" value={formData.ownership} onChange={handleChange} className={selectClass} data-testid="select-ownership">
            <option value="">Situation de propriété...</option>
            <option value="unique">Propriétaire unique</option>
            <option value="indivision">Indivision</option>
            <option value="succession">Succession</option>
          </select>
        )}

        {showIndivisaires && (
          <input type="text" name="indivisaires" placeholder="Nombre d'indivisaires (si applicable)" value={formData.indivisaires} onChange={handleChange} maxLength={50} className={inputClass} data-testid="input-indivisaires" />
        )}

        {showSuccessionStatus && (
          <select name="successionStatus" value={formData.successionStatus} onChange={handleChange} className={selectClass} data-testid="select-successionStatus">
            <option value="">Statut de la succession...</option>
            <option value="reglee">Succession réglée</option>
            <option value="en_cours">Succession en cours</option>
            <option value="inconnu">Je ne sais pas</option>
          </select>
        )}

        {showPrice && (
          <input type="text" name="desiredPrice" placeholder="Prix souhaité si connu (facultatif)" value={formData.desiredPrice} onChange={handleChange} maxLength={100} className={inputClass} data-testid="input-desiredPrice" />
        )}

        {showObjective && objectiveOptions.length > 0 && (
          <select name="objective" value={formData.objective} onChange={handleChange} className={selectClass} data-testid="select-objective">
            <option value="">Quel est votre objectif ?</option>
            {objectiveOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}

        {showSellProperty && (
          <select name="sellProperty" value={formData.sellProperty} onChange={handleChange} className={selectClass} data-testid="select-sellProperty">
            <option value="">Seriez-vous disposé à vendre également la parcelle ?</option>
            <option value="oui">Oui</option>
            <option value="peut-etre">Peut-être</option>
            <option value="non">Non</option>
          </select>
        )}

        {showTiming && (
          <select name="timing" value={formData.timing} onChange={handleChange} className={selectClass} data-testid="select-timing">
            <option value="">Dans quel délai souhaitez-vous agir ?</option>
            <option value="urgent">Dès que possible</option>
            <option value="3_mois">Moins de 3 mois</option>
            <option value="12_mois">3 à 12 mois</option>
            <option value="futur">Plus tard</option>
            <option value="information">Je me renseigne seulement</option>
          </select>
        )}

        {customFields}

        <textarea name="message" placeholder="Informations complémentaires (facultatif)" value={formData.message} onChange={handleChange} rows={4} maxLength={5000} className={`${inputClass} resize-none`} data-testid="input-message" />

        <div className="flex items-start">
          <input type="checkbox" name="consent" id={`consent-${formType}`} checked={formData.consent} onChange={handleChange} className="mt-1 mr-3 w-4 h-4 accent-gold" data-testid="checkbox-consent" required />
          <label htmlFor={`consent-${formType}`} className="font-sans text-sm text-gray-600 cursor-pointer">
            J'accepte que Sylva Invest me contacte pour traiter ma demande. Mes données seront traitées conformément à notre{' '}
            <a href="/politique-confidentialite" target="_blank" rel="noopener noreferrer" className="text-forest underline hover:text-gold">politique de confidentialité</a>.
          </label>
        </div>

        <button type="submit" className="btn-primary w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed" data-testid="submit-button" disabled={isSubmitting}>
          {isSubmitting ? "Envoi en cours..." : submitButtonText}
        </button>
      </form>
    </motion.div>
  );
};

export default LeadForm;
